/**
 * RequestInfoLandingPreview.tsx
 *
 * The live preview beside the request-info link generator: the landing page
 * exactly as a recipient's phone draws it, updated as the author picks the
 * resource, note, images and logos. It shows the page or the page after the
 * tap, as the editor asks.
 *
 * It renders the page's own component into an iframe rather than a div, because
 * the page is responsive to the WINDOW width. Inside a narrow div on a desktop
 * screen it would draw its desktop layout; inside a phone-width iframe the
 * breakpoints see a phone. The app's stylesheets are copied into the frame so
 * the page's classes resolve there too. In a narrow panel the whole phone is
 * scaled down, so the page inside keeps its phone layout.
 */
import { useCallback, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useElementSize } from '@/hooks/useElementSize';
import { cn } from '@/lib/utils';
import { RequestInfoPreview } from '@/pages/request-info/RequestInfoPage';
import { REQUEST_INFO_FONT_STYLESHEET } from '@/pages/request-info/requestInfo.service';
import type {
  RequestInfoBranding,
  RequestInfoConfirmLogoVariant,
  RequestInfoDetailsDisplay,
  RequestInfoLogoVariant,
  RequestInfoResource,
} from '@/pages/request-info/requestInfo.service';

/** An iPhone's CSS width, so the page draws its phone layout. */
const FRAME_WIDTH = 390;
/** The phone's bezel, each side. */
const FRAME_BORDER = 6;
/** The whole phone, bezel included: the width it is scaled down from. */
const FRAME_OUTER_WIDTH = FRAME_WIDTH + FRAME_BORDER * 2;
/** Its height at full size: a phone's, at most; less on a short screen, down to a floor. */
const FRAME_MAX_HEIGHT = 856;
const FRAME_MIN_HEIGHT = 320;
/** Before the panel is measured (and in jsdom). */
const FRAME_DEFAULT_HEIGHT = 652;

/** Shown in the greeting, so the author sees where the recipient's name goes. */
const SAMPLE_FIRST_NAME = 'Jane';

/**
 * A made-up facility phone (555 is no real area code), so the call line after
 * the tap reads as it does for a recipient: a campaign's send carries their
 * facility's number.
 */
const SAMPLE_FACILITY = { name: '', phone: '5555550123' };

const EMPTY_DOCUMENT = '<!DOCTYPE html><html><head></head><body></body></html>';

export type RequestInfoPreviewView = 'request' | 'confirmation';

export default function RequestInfoLandingPreview({
  view,
  onViewChange,
  resource,
  note,
  branding,
  heroImageUrl,
  logoVariant,
  confirmHeroImageUrl,
  confirmLogoVariant,
  showResourceImage,
  showResourceDescription,
  customDescription,
}: {
  /** Which page is shown; the editor switches it to the one being changed. */
  view: RequestInfoPreviewView;
  onViewChange: (view: RequestInfoPreviewView) => void;
  resource?: RequestInfoResource;
  note?: string;
  branding?: RequestInfoBranding;
  heroImageUrl?: string | null;
  logoVariant?: RequestInfoLogoVariant;
  confirmHeroImageUrl?: string | null;
  confirmLogoVariant?: RequestInfoConfirmLogoVariant;
} & RequestInfoDetailsDisplay) {
  return (
    // Fills the preview panel's height; the phone takes what the toggle and note leave.
    <div className="flex h-full min-h-0 w-full flex-col items-center">
      <div className="mb-3 flex w-full flex-shrink-0 items-center justify-between gap-3" style={{ maxWidth: FRAME_WIDTH }}>
        <h3 className="text-sm font-semibold text-slate-900">Preview</h3>
        <div role="group" aria-label="Preview page" className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5">
          {(
            [
              ['request', 'Page'],
              ['confirmation', 'After tapping'],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              aria-pressed={view === value}
              onClick={() => onViewChange(value)}
              className={cn(
                'rounded px-2.5 py-1 text-xs font-medium transition-colors',
                view === value ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700',
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <PhoneFrame title="Landing page preview">
        <RequestInfoPreview
          view={view}
          resource={resource}
          note={note}
          branding={branding}
          heroImageUrl={heroImageUrl}
          logoVariant={logoVariant}
          confirmHeroImageUrl={confirmHeroImageUrl}
          confirmLogoVariant={confirmLogoVariant}
          showResourceImage={showResourceImage}
          showResourceDescription={showResourceDescription}
          customDescription={customDescription}
          firstName={SAMPLE_FIRST_NAME}
          facility={SAMPLE_FACILITY}
        />
      </PhoneFrame>

      <p className="mt-2 flex-shrink-0 text-center text-xs text-slate-400" style={{ maxWidth: FRAME_WIDTH }}>
        Shown on a phone, with a sample first name and phone number. Recipients see their own name and their
        facility&apos;s number.
      </p>
    </div>
  );
}

/**
 * An iframe a phone's width, with the children rendered into its document. It
 * is as tall as the panel allows (up to a phone's height); the page scrolls
 * inside it.
 *
 * In a panel narrower than the phone, the phone is scaled down as a whole
 * rather than narrowed: the page inside still lays out at 390px, so it keeps
 * its phone layout and is only drawn smaller.
 */
function PhoneFrame({ title, children }: { title: string; children: ReactNode }) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const areaRef = useRef<HTMLDivElement>(null);
  const [body, setBody] = useState<HTMLElement | null>(null);

  // 0 until measured (and in jsdom): full size, at the height a phone would be.
  const area = useElementSize(areaRef);
  const scale = area.width > 0 ? Math.min(1, area.width / FRAME_OUTER_WIDTH) : 1;
  const height = area.height > 0
    ? Math.min(FRAME_MAX_HEIGHT, Math.max(FRAME_MIN_HEIGHT, Math.floor(area.height / scale)))
    : FRAME_DEFAULT_HEIGHT;

  const handleLoad = useCallback(() => {
    const doc = frameRef.current?.contentDocument;
    if (!doc) return;
    // Rebuilt from scratch on every load, so a second load never doubles the styles.
    doc.head.replaceChildren();
    const base = doc.createElement('base');
    base.href = `${window.location.origin}/`;
    doc.head.appendChild(base);
    document.querySelectorAll('style, link[rel="stylesheet"]').forEach((node) => {
      doc.head.appendChild(node.cloneNode(true));
    });
    const fonts = doc.createElement('link');
    fonts.rel = 'stylesheet';
    fonts.href = REQUEST_INFO_FONT_STYLESHEET;
    fonts.referrerPolicy = 'no-referrer';
    doc.head.appendChild(fonts);
    // Nothing in the preview is clickable: a link would navigate the frame away.
    const inert = doc.createElement('style');
    inert.textContent = 'body { margin: 0; } a, button { pointer-events: none; }';
    doc.head.appendChild(inert);
    setBody(doc.body);
  }, []);

  return (
    // The space the phone may take: all the width, and the height left between
    // the toggle above and the note below.
    <div ref={areaRef} className="flex min-h-0 w-full flex-1 justify-center">
      {/* The room the scaled phone takes up, so the layout around it is sized to what is drawn. */}
      <div className="flex-shrink-0" style={{ width: FRAME_OUTER_WIDTH * scale, height: height * scale }}>
        <div
          className="origin-top-left overflow-hidden rounded-[2rem] border-slate-800 bg-white shadow-lg"
          style={{
            width: FRAME_OUTER_WIDTH,
            height,
            borderWidth: FRAME_BORDER,
            transform: scale < 1 ? `scale(${scale})` : undefined,
          }}
        >
          <iframe
            ref={frameRef}
            title={title}
            srcDoc={EMPTY_DOCUMENT}
            onLoad={handleLoad}
            width={FRAME_WIDTH}
            className="block h-full border-0"
          />
          {body && createPortal(children, body)}
        </div>
      </div>
    </div>
  );
}
